// src/store/slices/uiSlice.ts
/**
 * UI state management - active tools, dialogs, selections
 * Implements Section 5.7 of the specification
 * Updated for T-02, T-22, T-24 with new features while preserving existing logic
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { UIState, ToolType } from '../../types/redux';

// Storage key for tool settings
const TOOL_SETTINGS_STORAGE_KEY = 'nl:toolSettings';

// Default tool settings
const defaultToolSettings: UIState['toolSettings'] = {
  select: { color: '#3b82f6', thickness: 2 },
  pan: { color: '#3b82f6', thickness: 2 },
  jump: { color: '#ef4444', thickness: 3 },
  text: { color: '#000000', thickness: 2 },
  equation: { color: '#000000', thickness: 2 },
  pen: { color: '#000000', thickness: 2 },
  highlighter: { color: '#fbbf24', thickness: 8 },
  eraser: { color: '#ffffff', thickness: 10 },
  rectangle: { color: '#3b82f6', thickness: 2 },
  'rectangle-filled': { color: '#3b82f6', thickness: 2 },
  circle: { color: '#3b82f6', thickness: 2 },
  line: { color: '#3b82f6', thickness: 2, arrowStart: false, arrowEnd: false },
  measurement: { color: '#10b981', thickness: 2 },
};

/**
 * Load tool settings from localStorage
 */
function loadToolSettingsFromStorage(): UIState['toolSettings'] {
  try {
    const stored = localStorage.getItem(TOOL_SETTINGS_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Merge with defaults to handle new tool additions
      const toolSettings = { ...defaultToolSettings, ...parsed };
      console.log('🎨 Loaded tool settings:', toolSettings);
      return toolSettings;
    }
  } catch (error) {
    console.warn('Failed to load tool settings from storage:', error);
  }
  console.log('🎨 Using default tool settings:', defaultToolSettings);
  return defaultToolSettings;
}

/**
 * Save tool settings to localStorage
 */
function saveToolSettingsToStorage(toolSettings: UIState['toolSettings']): void {
  try {
    localStorage.setItem(TOOL_SETTINGS_STORAGE_KEY, JSON.stringify(toolSettings));
  } catch (error) {
    console.warn('Failed to save tool settings to storage:', error);
  }
}

// Load tool settings first
const loadedToolSettings = loadToolSettingsFromStorage();

const initialState: UIState = {
  activeTool: 'select',
  selectedObjects: [],
  dialogs: {
    shareDialog: false,
    loadDialog: false,
    scaleFactorDialog: false,
    jumpToValueDialog: false,
    exportDialog: false,
    preferencesDialog: false, // T-24: Added preferences dialog
  },
  modals: {
    exportDialog: false,
    helpDialog: false,
  },
  panels: {
    constants: false,
    expressions: false,
    annotations: false,
  },
  colorPicker: {
    open: false,
    activeColor: loadedToolSettings.select.color, // Use loaded settings
  },
  // Per-tool settings with localStorage persistence
  toolSettings: loadedToolSettings,
  // Legacy global settings (for backward compatibility)
  thickness: loadedToolSettings.select.thickness, // Use loaded settings
  loading: false,
  dragSnap: {
    enabled: true,
    threshold: 15,              // 15 pixel snap threshold
    showIndicators: true,
    snapToTicks: true,
    snapToCustomTicks: true,
  },
  // T-16: Measurement tool initial state
  measurement: {
    isActive: false,
    isDragging: false,
    measurements: [],
    nextId: 1,
  },
  // T-18: Annotation layer settings
  annotationLayer: {
    panWithNumberLine: true, // Default to panning with number line
  },
  // T-22: Share URL state
  shareUrlState: {
    detectedSceneId: null,
    isAutoLoading: false,
    autoLoadError: null,
    showAutoLoadPrompt: false,
  },
  // T-24: Notifications and accessibility
  notifications: [],
  keyboardShortcutsEnabled: true,
  accessibilityMode: false,
  mainLabelsHidden: false,
  tickSubdivisionMode: undefined,
  showEquivalentIntegerLine: true,
  addPointDialog: {
    isOpen: false,
    targetLine: 'primary',
    initialValue: undefined,
  },
};

export const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    /**
     * Set the active tool
     */
    setActiveTool: (state, action: PayloadAction<ToolType>) => {
      const newTool = action.payload;
      state.activeTool = newTool;
      // Clear selections when switching tools
      state.selectedObjects = [];
      // Load tool-specific settings
      const toolSettings = state.toolSettings[newTool];
      state.colorPicker.activeColor = toolSettings.color;
      state.thickness = toolSettings.thickness;
    },

    /**
     * Set selected objects
     */
    setSelectedObjects: (state, action: PayloadAction<string[]>) => {
      state.selectedObjects = action.payload;
    },

    /**
     * Add object to selection
     */
    addToSelection: (state, action: PayloadAction<string>) => {
      if (!state.selectedObjects.includes(action.payload)) {
        state.selectedObjects.push(action.payload);
      }
    },

    /**
     * Remove object from selection
     */
    removeFromSelection: (state, action: PayloadAction<string>) => {
      state.selectedObjects = state.selectedObjects.filter(id => id !== action.payload);
    },

    /**
     * Clear all selections
     */
    clearSelection: (state) => {
      state.selectedObjects = [];
    },

    /**
     * Toggle dialog visibility
     */
    toggleDialog: (state, action: PayloadAction<keyof UIState['dialogs']>) => {
      const dialogKey = action.payload;
      state.dialogs[dialogKey] = !state.dialogs[dialogKey];
    },

    /**
     * Set dialog visibility
     */
    setDialogOpen: (state, action: PayloadAction<{ dialog: keyof UIState['dialogs']; open: boolean }>) => {
      const { dialog, open } = action.payload;
      state.dialogs[dialog] = open;
    },

    /**
     * Set dialog state explicitly (T-24: New alias for setDialogOpen)
     */
    setDialogState: (state, action: PayloadAction<{ dialog: keyof UIState['dialogs']; open: boolean }>) => {
      const { dialog, open } = action.payload;
      state.dialogs[dialog] = open;
    },

    /**
     * Toggle a modal's open/closed state (T-24: New)
     */
    toggleModal: (state, action: PayloadAction<keyof UIState['modals']>) => {
      const modalName = action.payload;
      state.modals[modalName] = !state.modals[modalName];
    },

    /**
     * Set modal state explicitly (T-24: New)
     */
    setModalState: (state, action: PayloadAction<{ modal: keyof UIState['modals']; open: boolean }>) => {
      const { modal, open } = action.payload;
      state.modals[modal] = open;
    },

    /**
     * Toggle a panel's open/closed state (T-24: New)
     */
    togglePanel: (state, action: PayloadAction<keyof UIState['panels']>) => {
      const panelName = action.payload;
      state.panels[panelName] = !state.panels[panelName];
    },

    /**
     * Set panel state explicitly (T-24: New)
     */
    setPanelState: (state, action: PayloadAction<{ panel: keyof UIState['panels']; open: boolean }>) => {
      const { panel, open } = action.payload;
      state.panels[panel] = open;
    },

    /**
     * Toggle color picker
     */
    toggleColorPicker: (state) => {
      state.colorPicker.open = !state.colorPicker.open;
    },

    /**
     * Set active color
     */
    setActiveColor: (state, action: PayloadAction<string>) => {
      state.colorPicker.activeColor = action.payload;
      // Also update the current tool's color setting
      state.toolSettings[state.activeTool].color = action.payload;
      // Save to localStorage
      saveToolSettingsToStorage(state.toolSettings);
    },

    /**
     * Set drawing thickness
     */
    setThickness: (state, action: PayloadAction<number>) => {
      const thickness = Math.max(1, Math.min(20, action.payload));
      state.thickness = thickness; // Legacy support
      // Also update the current tool's thickness setting
      state.toolSettings[state.activeTool].thickness = thickness;
      // Save to localStorage
      saveToolSettingsToStorage(state.toolSettings);
    },

    /**
     * Set tool-specific color
     */
    setToolColor: (state, action: PayloadAction<{ tool: ToolType; color: string }>) => {
      const { tool, color } = action.payload;
      state.toolSettings[tool].color = color;
      // Update active color if this is the current tool
      if (tool === state.activeTool) {
        state.colorPicker.activeColor = color;
      }
      // Save to localStorage
      saveToolSettingsToStorage(state.toolSettings);
    },

    /**
     * Set tool-specific thickness
     */
    setToolThickness: (state, action: PayloadAction<{ tool: ToolType; thickness: number }>) => {
      const { tool, thickness } = action.payload;
      const clampedThickness = Math.max(1, Math.min(20, thickness));
      state.toolSettings[tool].thickness = clampedThickness;
      // Update global thickness if this is the current tool
      if (tool === state.activeTool) {
        state.thickness = clampedThickness;
      }
      // Save to localStorage
      saveToolSettingsToStorage(state.toolSettings);
    },

    /**
     * Set tool-specific arrow start setting
     */
    setToolArrowStart: (state, action: PayloadAction<{ tool: ToolType; arrowStart: boolean }>) => {
      const { tool, arrowStart } = action.payload;
      if (state.toolSettings[tool]) {
        state.toolSettings[tool].arrowStart = arrowStart;
        // Save to localStorage
        saveToolSettingsToStorage(state.toolSettings);
      }
    },

    /**
     * Set tool-specific arrow end setting
     */
    setToolArrowEnd: (state, action: PayloadAction<{ tool: ToolType; arrowEnd: boolean }>) => {
      const { tool, arrowEnd } = action.payload;
      if (state.toolSettings[tool]) {
        state.toolSettings[tool].arrowEnd = arrowEnd;
        // Save to localStorage
        saveToolSettingsToStorage(state.toolSettings);
      }
    },

    /**
     * Set error message
     */
    setErrorMessage: (state, action: PayloadAction<string | undefined>) => {
      state.errorMessage = action.payload;
    },

    /**
     * Set loading state
     */
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },

    /**
     * Close all dialogs
     */
    closeAllDialogs: (state) => {
      Object.keys(state.dialogs).forEach(key => {
        state.dialogs[key as keyof UIState['dialogs']] = false;
      });
    },

    /**
     * Set snap enabled/disabled
     */
    setSnapEnabled: (state, action: PayloadAction<boolean>) => {
      state.dragSnap.enabled = action.payload;
    },

    /**
     * Set snap threshold
     */
    setSnapThreshold: (state, action: PayloadAction<number>) => {
      state.dragSnap.threshold = action.payload;
    },

    /**
     * Set snap indicators visibility
     */
    setSnapIndicators: (state, action: PayloadAction<boolean>) => {
      state.dragSnap.showIndicators = action.payload;
    },

    /**
     * Set snap to ticks
     */
    setSnapToTicks: (state, action: PayloadAction<boolean>) => {
      state.dragSnap.snapToTicks = action.payload;
    },

    /**
     * Set snap to custom ticks
     */
    setSnapToCustomTicks: (state, action: PayloadAction<boolean>) => {
      state.dragSnap.snapToCustomTicks = action.payload;
    },

    // T-16: Measurement tool actions
    /**
     * Start a new measurement
     * Phase 6: Extended for 2D coordinate grid
     */
    startMeasurement: (state, action: PayloadAction<{ 
      x: number;
      y?: number; // Phase 6: Added for 2D
      value: number; 
      isSnapped?: boolean; 
      snappedValue?: number 
    }>) => {
      state.measurement = {
        ...state.measurement,
        isActive: true,
        startPoint: action.payload,
        endPoint: undefined,
        isDragging: false,
      };
    },

    /**
     * Update measurement endpoint during drag
     * Phase 6: Extended for 2D coordinate grid
     */
    updateMeasurement: (state, action: PayloadAction<{ 
      x: number;
      y?: number; // Phase 6: Added for 2D
      value: number; 
      isSnapped?: boolean; 
      snappedValue?: number 
    }>) => {
      if (state.measurement.isActive) {
        state.measurement.endPoint = action.payload;
        state.measurement.isDragging = true;
      }
    },

    /**
     * Finish measurement (stop dragging)
     */
    finishMeasurement: (state) => {
      state.measurement.isDragging = false;
    },

    /**
     * Cancel measurement (clear all state)
     */
    cancelMeasurement: (state) => {
      state.measurement = { 
        ...state.measurement,
        isActive: false, 
        isDragging: false,
        startPoint: undefined,
        endPoint: undefined,
      };
    },

    /**
     * Complete measurement and add to list
     * Phase 6: Extended for 2D coordinate grid
     */
    completeMeasurement: (state, action: PayloadAction<{
      isSnapped?: boolean;
      snappedStartValue?: number;
      snappedEndValue?: number;
      // Phase 6: 2D support
      point1?: { x: number; y: number };
      point2?: { x: number; y: number };
      showCalculation?: boolean;
      showComponents?: boolean;
      displayMode?: 'exact' | 'decimal' | 'both';
    }>) => {
      if (state.measurement.startPoint && state.measurement.endPoint) {
        const { 
          isSnapped, 
          snappedStartValue, 
          snappedEndValue,
          point1,
          point2,
          showCalculation = false,
          showComponents = false,
          displayMode = 'decimal',
        } = action.payload;
        const newMeasurement = {
          id: `measurement-${state.measurement.nextId}`,
          startValue: state.measurement.startPoint.value,
          endValue: state.measurement.endPoint.value,
          selected: false,
          isSnapped,
          snappedStartValue,
          snappedEndValue,
          // Phase 6: 2D support
          point1,
          point2,
          showCalculation,
          showComponents,
          displayMode,
        };
        state.measurement.measurements.push(newMeasurement);
        state.measurement.nextId += 1;
        state.measurement.isActive = false;
        state.measurement.isDragging = false;
        state.measurement.startPoint = undefined;
        state.measurement.endPoint = undefined;
      }
    },

    /**
     * Select/deselect measurement
     */
    toggleMeasurementSelection: (state, action: PayloadAction<{ id: string; ctrlKey?: boolean }>) => {
      const { id, ctrlKey } = action.payload;
      const measurement = state.measurement.measurements.find(m => m.id === id);
      if (measurement) {
        if (!ctrlKey) {
          // Clear all selections if Ctrl is not held
          state.measurement.measurements.forEach(m => m.selected = false);
        }
        measurement.selected = !measurement.selected;
      }
    },

    /**
     * Delete selected measurements
     */
    deleteSelectedMeasurements: (state) => {
      state.measurement.measurements = state.measurement.measurements.filter(m => !m.selected);
    },

    /**
     * Clear all measurements
     */
    clearAllMeasurements: (state) => {
      state.measurement.measurements = [];
    },

    /**
     * Load measurements state from shared scene
     */
    loadMeasurementsState: (state, action: PayloadAction<any[]>) => {
      state.measurement.measurements = (action.payload || []).map(m => ({
        ...m,
        selected: false, // Reset selection on load
      }));
      // Reset transient measurement state
      state.measurement.isActive = false;
      state.measurement.isDragging = false;
      state.measurement.startPoint = undefined;
      state.measurement.endPoint = undefined;
    },

    /**
     * Toggle annotation layer panning with number line
     */
    toggleAnnotationLayerPanning: (state) => {
      state.annotationLayer.panWithNumberLine = !state.annotationLayer.panWithNumberLine;
    },

    /**
     * Set annotation layer panning with number line
     */
    setAnnotationLayerPanning: (state, action: PayloadAction<boolean>) => {
      state.annotationLayer.panWithNumberLine = action.payload;
    },

    // T-22: Share URL state actions
    /**
     * Set detected scene ID from URL (T-22)
     */
    setDetectedSceneId: (state, action: PayloadAction<string | null>) => {
      state.shareUrlState.detectedSceneId = action.payload;
      state.shareUrlState.showAutoLoadPrompt = action.payload !== null;
    },

    /**
     * Set auto-loading state (T-22)
     */
    setAutoLoadingState: (state, action: PayloadAction<boolean>) => {
      state.shareUrlState.isAutoLoading = action.payload;
    },

    /**
     * Set auto-load error (T-22)
     */
    setAutoLoadError: (state, action: PayloadAction<string | null>) => {
      state.shareUrlState.autoLoadError = action.payload;
    },

    /**
     * Dismiss auto-load prompt (T-22)
     */
    dismissAutoLoadPrompt: (state) => {
      state.shareUrlState.showAutoLoadPrompt = false;
      state.shareUrlState.detectedSceneId = null;
      state.shareUrlState.autoLoadError = null;
    },

    /**
     * Clear share URL state (T-22)
     */
    clearShareUrlState: (state) => {
      state.shareUrlState = {
        detectedSceneId: null,
        isAutoLoading: false,
        autoLoadError: null,
        showAutoLoadPrompt: false,
      };
    },

    // T-24: Notifications and accessibility actions
    /**
     * Add notification (T-24)
     */
    addNotification: (state, action: PayloadAction<{
      id: string;
      type: 'info' | 'success' | 'warning' | 'error';
      title: string;
      message?: string;
      duration?: number;
    }>) => {
      state.notifications.push(action.payload);
    },

    /**
     * Remove notification (T-24)
     */
    removeNotification: (state, action: PayloadAction<string>) => {
      state.notifications = state.notifications.filter(n => n.id !== action.payload);
    },

    /**
     * Clear all notifications (T-24)
     */
    clearNotifications: (state) => {
      state.notifications = [];
    },

    /**
     * Toggle keyboard shortcuts (T-24)
     */
    toggleKeyboardShortcuts: (state) => {
      state.keyboardShortcutsEnabled = !state.keyboardShortcutsEnabled;
    },

    /**
     * Set keyboard shortcuts state (T-24)
     */
    setKeyboardShortcuts: (state, action: PayloadAction<boolean>) => {
      state.keyboardShortcutsEnabled = action.payload;
    },

    /**
     * Toggle accessibility mode (T-24)
     */
    toggleAccessibilityMode: (state) => {
      state.accessibilityMode = !state.accessibilityMode;
    },

    /**
     * Set accessibility mode (T-24)
     */
    setAccessibilityMode: (state, action: PayloadAction<boolean>) => {
      state.accessibilityMode = action.payload;
    },

    /**
     * Reset tool settings to defaults
     */
    resetToolSettings: (state) => {
      state.toolSettings = { ...defaultToolSettings };
      // Update current tool's active settings
      const currentToolSettings = state.toolSettings[state.activeTool];
      state.colorPicker.activeColor = currentToolSettings.color;
      state.thickness = currentToolSettings.thickness;
      // Save to localStorage
      saveToolSettingsToStorage(state.toolSettings);
    },

    /**
     * Toggle global visibility of primary number line labels
     */
    toggleMainLabelsHidden: (state) => {
      state.mainLabelsHidden = !state.mainLabelsHidden;
    },

    /**
     * Session-only tick subdivision mode override
     */
    setTickSubdivisionMode: (state, action: PayloadAction<UIState['tickSubdivisionMode']>) => {
      state.tickSubdivisionMode = action.payload;
    },

    /**
     * Set visibility of the integer-only helper line in equivalent fractions mode
     */
    setShowEquivalentIntegerLine: (state, action: PayloadAction<boolean>) => {
      state.showEquivalentIntegerLine = action.payload;
    },

    /**
     * Load tool settings from localStorage (for manual refresh)
     */
    loadToolSettings: (state) => {
      const loadedSettings = loadToolSettingsFromStorage();
      state.toolSettings = loadedSettings;
      // Update current tool's active settings
      const currentToolSettings = state.toolSettings[state.activeTool];
      state.colorPicker.activeColor = currentToolSettings.color;
      state.thickness = currentToolSettings.thickness;
    },

    /**
     * Open the Add Point dialog with optional initial values
     */
    openAddPointDialog: (state, action: PayloadAction<{ targetLine?: 'primary' | 'secondary'; initialValue?: number }>) => {
      state.addPointDialog = {
        isOpen: true,
        targetLine: action.payload.targetLine || 'primary',
        initialValue: action.payload.initialValue,
      };
    },

    /**
     * Close the Add Point dialog
     */
    closeAddPointDialog: (state) => {
      if (state.addPointDialog) {
        state.addPointDialog.isOpen = false;
      }
    },
  },
});

export const {
  setActiveTool,
  setSelectedObjects,
  addToSelection,
  removeFromSelection,
  clearSelection,
  toggleDialog,
  setDialogOpen,
  setDialogState,
  toggleModal,
  setModalState,
  togglePanel,
  setPanelState,
  toggleColorPicker,
  setActiveColor,
  setThickness,
  setToolColor,
  setToolThickness,
  setToolArrowStart,
  setToolArrowEnd,
  setErrorMessage,
  setLoading,
  closeAllDialogs,
  setSnapEnabled,
  setSnapThreshold,
  setSnapIndicators,
  setSnapToTicks,
  setSnapToCustomTicks,
  // T-16: Measurement tool actions
  startMeasurement,
  updateMeasurement,
  finishMeasurement,
  cancelMeasurement,
  completeMeasurement,
  toggleMeasurementSelection,
  deleteSelectedMeasurements,
  clearAllMeasurements,
  loadMeasurementsState,
  // T-18: Annotation layer actions
  toggleAnnotationLayerPanning,
  setAnnotationLayerPanning,
  // T-22: Share URL state actions
  setDetectedSceneId,
  setAutoLoadingState,
  setAutoLoadError,
  dismissAutoLoadPrompt,
  clearShareUrlState,
  // T-24: Notifications and accessibility actions
  addNotification,
  removeNotification,
  clearNotifications,
  toggleKeyboardShortcuts,
  setKeyboardShortcuts,
  toggleAccessibilityMode,
  setAccessibilityMode,
  // Tool settings persistence actions
  resetToolSettings,
  loadToolSettings,
  setTickSubdivisionMode,
  toggleMainLabelsHidden,
  setShowEquivalentIntegerLine,
  // Add Point dialog actions
  openAddPointDialog,
  closeAddPointDialog,
} = uiSlice.actions;

export default uiSlice.reducer;